import { decodePng } from './png-codec.js';

export type HexColor = string;
export type RGBAInput = { r: number; g: number; b: number; a?: number };
export type ColorInput = HexColor | RGBAInput;
export type CoverageRect = { x: number; y: number; width: number; height: number };

/**
 * Query describing which colors to measure. Keep this free of assertions.
 * Use the returned analysis to assert in your tests.
 */
export interface ColorCoverageQuery {
  /** One or more colors considered a match (hex like '#ff00aa' or {r,g,b,a?}) */
  colors: ColorInput | ColorInput[];
  /** Per-channel color tolerance (defaults to `defaultTolerance`) */
  tolerance?: number;
}

// Deprecated: retained for backward-compat wrappers
export interface ColorCoverageExpectation {
  label?: string;
  colors: ColorInput | ColorInput[];
  minPercent?: number;
  maxPercent?: number;
  targetPercent?: number;
  targetPercentTolerance?: number;
  tolerance?: number;
  minPixels?: number;
  maxPixels?: number;
  targetPixels?: number;
  targetPixelsTolerance?: number;
}

export interface ColorCoverageOptions {
  /** Region of interest – defaults to the entire image */
  region?: CoverageRect;
  /** Ignore pixels whose alpha channel is below this threshold (0-255). Default: 1 */
  alphaMin?: number;
  /** Skip pixels that are near black before applying expectations */
  excludeNearBlack?: boolean;
  /** Closeness threshold (0-1) to treat a pixel as black when `excludeNearBlack` is true. Default: 0.9 */
  blackClosenessThreshold?: number;
  /** Sample every Nth pixel in both axes to speed up large screenshots. Default: 1 */
  sampleEveryNthPixel?: number;
  /** Default per-channel color tolerance when an expectation omits `tolerance`. Default: 3 */
  defaultTolerance?: number;
  /** Default percent tolerance (+/-) when `targetPercent` is provided without `targetPercentTolerance`. Default: 0.5 */
  defaultPercentTolerance?: number;
  /** Default pixel tolerance (+/-) when `targetPixels` is provided without `targetPixelsTolerance`. Default: 0 */
  defaultPixelTolerance?: number;
  /** Whether a pixel can satisfy multiple expectations. Default: false */
  allowMultipleRuleMatches?: boolean;
  /** Include fully transparent or excluded pixels in the denominator. Default: false */
  includeExcludedInPercentages?: boolean;
}

export interface ColorCoverageMatchSummary {
  colors: HexColor[];
  pixelCount: number;
  percent: number;
  tolerance: number;
}

export interface ColorCoverageAnalysis {
  width: number;
  height: number;
  region: CoverageRect;
  totalSampledPixels: number;
  consideredPixels: number;
  excludedTransparentPixels: number;
  excludedNearBlackPixels: number;
  matches: ColorCoverageMatchSummary[];
  unmatchedPixelCount: number;
  unmatchedPercent: number;
}

export class ColorCoverageError extends Error {
  constructor(message: string, public analysis: ColorCoverageAnalysis) {
    super(message);
    this.name = 'ColorCoverageError';
  }
}

const MAX_RGB = Math.sqrt(3 * 255 * 255);

function normalizeHex(hex: HexColor): string {
  let value = hex.trim();
  if (!value.startsWith('#')) {
    value = `#${value}`;
  }
  if (!/^#[0-9a-fA-F]{6}$/.test(value)) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  return value.toLowerCase();
}

function hexToRgb(hex: HexColor) {
  const normalized = normalizeHex(hex);
  const num = parseInt(normalized.slice(1), 16);
  return {
    r: (num >> 16) & 0xff,
    g: (num >> 8) & 0xff,
    b: num & 0xff,
  };
}

function clamp255(n: number): number { return Math.max(0, Math.min(255, Math.round(n))); }
function componentFromUnitOrByte(v: number): number {
  // Accept 0-1 or 0-255; detect unit if <= 1 with non-integer allowed
  if (v <= 1) return clamp255(v * 255);
  return clamp255(v);
}

export function toRGB(input: ColorInput): { r: number; g: number; b: number } {
  if (typeof input === 'string') return hexToRgb(input);
  const r = componentFromUnitOrByte(input.r);
  const g = componentFromUnitOrByte(input.g);
  const b = componentFromUnitOrByte(input.b);
  // Alpha is ignored for matching; default opaque if missing
  return { r, g, b };
}

export function rgbToHex(rgb: { r: number; g: number; b: number }): string {
  const r = clamp255(rgb.r).toString(16).padStart(2, '0');
  const g = clamp255(rgb.g).toString(16).padStart(2, '0');
  const b = clamp255(rgb.b).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`.toLowerCase();
}

function channelDistance(a: number, b: number): number {
  return Math.abs(a - b);
}

function isColorMatch(
  pixel: { r: number; g: number; b: number },
  target: { r: number; g: number; b: number },
  tolerance: number
): boolean {
  return (
    channelDistance(pixel.r, target.r) <= tolerance &&
    channelDistance(pixel.g, target.g) <= tolerance &&
    channelDistance(pixel.b, target.b) <= tolerance
  );
}

function blackCloseness(r: number, g: number, b: number): number {
  const magnitude = Math.sqrt(r * r + g * g + b * b);
  return 1 - magnitude / MAX_RGB;
}

function clampRegion(region: CoverageRect, width: number, height: number): CoverageRect {
  const x = Math.max(0, Math.min(width - 1, Math.floor(region.x)));
  const y = Math.max(0, Math.min(height - 1, Math.floor(region.y)));
  const w = Math.max(1, Math.min(width - x, Math.floor(region.width)));
  const h = Math.max(1, Math.min(height - y, Math.floor(region.height)));
  return { x, y, width: w, height: h };
}

function prepareQueries(
  queries: ColorCoverageQuery[],
  options: ColorCoverageOptions
): Array<{
  colors: HexColor[];
  rgb: Array<{ r: number; g: number; b: number }>;
  tolerance: number;
}> {
  const defaultTolerance = options.defaultTolerance ?? 3;
  return queries.map((exp) => {
    const inputs: ColorInput[] = Array.isArray(exp.colors) ? exp.colors : [exp.colors];
    const rgb = inputs.map(toRGB);
    const normalizedColors = rgb.map(rgbToHex);
    const tolerance = exp.tolerance ?? defaultTolerance;
    return {
      colors: normalizedColors,
      rgb,
      tolerance,
    };
  });
}

export function analyzeColorCoverage(
  imageBuffer: Buffer | Uint8Array,
  queries: Array<ColorCoverageQuery | ColorCoverageExpectation>,
  options: ColorCoverageOptions = {}
): ColorCoverageAnalysis {
  const decoded = decodePng(imageBuffer);
  const width = decoded.width;
  const height = decoded.height;
  const data = decoded.data;

  const region = clampRegion(options.region ?? { x: 0, y: 0, width, height }, width, height);
  const alphaMin = options.alphaMin ?? 1;
  const excludeNearBlack = options.excludeNearBlack ?? false;
  const blackThreshold = options.blackClosenessThreshold ?? 0.9;
  const sampleEvery = Math.max(1, Math.floor(options.sampleEveryNthPixel ?? 1));
  const includeExcludedInPercentages = options.includeExcludedInPercentages ?? false;
  const allowOverlap = options.allowMultipleRuleMatches ?? false;

  const prepared = prepareQueries(queries as ColorCoverageQuery[], options);
  const matchCounts = new Array(prepared.length).fill(0);

  let totalSampledPixels = 0;
  let consideredPixels = 0;
  let excludedTransparentPixels = 0;
  let excludedNearBlackPixels = 0;
  const xEnd = region.x + region.width;
  const yEnd = region.y + region.height;
  for (let y = region.y; y < yEnd; y += sampleEvery) {
    for (let x = region.x; x < xEnd; x += sampleEvery) {
      totalSampledPixels++;
      const idx = (y * width + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const a = data[idx + 3];

      if (a < alphaMin) {
        excludedTransparentPixels++;
        continue;
      }

      if (excludeNearBlack && blackCloseness(r, g, b) >= blackThreshold) {
        excludedNearBlackPixels++;
        continue;
      }

      consideredPixels++;
      const pixel = { r, g, b };
      let matchedAny = false;

      for (let i = 0; i < prepared.length; i++) {
        const rule = prepared[i];
        for (let c = 0; c < rule.rgb.length; c++) {
          if (isColorMatch(pixel, rule.rgb[c], rule.tolerance)) {
            matchCounts[i]++;
            matchedAny = true;
            break;
          }
        }
        if (!allowOverlap && matchedAny) {
          break;
        }
      }
    }
  }

  const denominator = includeExcludedInPercentages
    ? totalSampledPixels
    : Math.max(1, consideredPixels);

  const matches: ColorCoverageMatchSummary[] = prepared.map((rule, idx) => {
    const pixelCount = matchCounts[idx];
    const percent = (pixelCount / denominator) * 100;
    return {
      colors: rule.colors,
      pixelCount,
      percent,
      tolerance: rule.tolerance,
    };
  });

  const matchedPixelCount = allowOverlap
    ? Math.min(consideredPixels, matchCounts.reduce((sum, v) => sum + v, 0))
    : matchCounts.reduce((sum, v) => sum + v, 0);

  const unmatchedPixelCount = Math.max(0, consideredPixels - matchedPixelCount);
  const unmatchedPercent = (unmatchedPixelCount / denominator) * 100;

  return {
    width,
    height,
    region,
    totalSampledPixels,
    consideredPixels,
    excludedTransparentPixels,
    excludedNearBlackPixels,
    matches,
    unmatchedPixelCount,
    unmatchedPercent,
  };
}

export function assertColorCoverage(
  imageBuffer: Buffer | Uint8Array,
  expectations: ColorCoverageExpectation | ColorCoverageExpectation[],
  options: ColorCoverageOptions = {}
): ColorCoverageAnalysis {
  const expList = Array.isArray(expectations) ? expectations : [expectations];
  if (!expList.length) {
    throw new Error('assertColorCoverage requires at least one expectation');
  }

  // Run analysis using just the color/tolerance configuration (ignore expectation thresholds)
  const queries: ColorCoverageQuery[] = expList.map((e) => ({ colors: e.colors, tolerance: e.tolerance }));
  const analysis = analyzeColorCoverage(imageBuffer, queries, options);

  const failureMessages: string[] = [];

  expList.forEach((exp, idx) => {
    const match = analysis.matches[idx];
    const label = exp.label ?? `expectation-${idx + 1}`;
    if (!match) {
      failureMessages.push(`${label} had no analysis result`);
      return;
    }
    if (typeof exp.minPercent === 'number' && match.percent < exp.minPercent) {
      failureMessages.push(`${label} expected >= ${exp.minPercent}% but measured ${match.percent.toFixed(2)}%`);
    }
    if (typeof exp.maxPercent === 'number' && match.percent > exp.maxPercent) {
      failureMessages.push(`${label} expected <= ${exp.maxPercent}% but measured ${match.percent.toFixed(2)}%`);
    }
    if (typeof exp.targetPercent === 'number') {
      const tolerance = exp.targetPercentTolerance ?? options.defaultPercentTolerance ?? 0.5;
      const delta = Math.abs(match.percent - exp.targetPercent);
      if (delta > tolerance) {
        failureMessages.push(`${label} expected ${exp.targetPercent}% ±${tolerance}% but measured ${match.percent.toFixed(2)}%`);
      }
    }
    if (typeof exp.minPixels === 'number' && match.pixelCount < exp.minPixels) {
      failureMessages.push(`${label} expected >= ${exp.minPixels}px but measured ${match.pixelCount}`);
    }
    if (typeof exp.maxPixels === 'number' && match.pixelCount > exp.maxPixels) {
      failureMessages.push(`${label} expected <= ${exp.maxPixels}px but measured ${match.pixelCount}`);
    }
    if (typeof exp.targetPixels === 'number') {
      const tol = exp.targetPixelsTolerance ?? options.defaultPixelTolerance ?? 0;
      const deltaPx = Math.abs(match.pixelCount - exp.targetPixels);
      if (deltaPx > tol) {
        failureMessages.push(`${label} expected ${exp.targetPixels}px ±${tol}px but measured ${match.pixelCount}px`);
      }
    }
  });

  if (failureMessages.length) {
    throw new ColorCoverageError(failureMessages.join('; '), analysis);
  }

  return analysis;
}

// Options for connected-color bounding rect detection. Shares knobs with ColorCoverageOptions where practical.
export interface ConnectedColorsOptions extends Omit<
  ColorCoverageOptions,
  'sampleEveryNthPixel' | 'allowMultipleRuleMatches' | 'includeExcludedInPercentages'
> {
  /** One or more colors considered part of the connected region(s) (hex or {r,g,b,a?}) */
  colors: ColorInput[];
  /** Per-channel color tolerance */
  tolerance?: number;
  /** Alpha threshold (0-255); pixels below are ignored entirely */
  alphaMin?: number;
  /** 4- or 8-connected adjacency */
  adjacency?: 4 | 8;
  /** Discard components with fewer matched pixels than this. Default: 1 */
  minRegionPixels?: number;
  /** Minimum percent (0-100) of the bounding box that must be covered by the matched colors */
  colorsMinPercentageOfRect?: number;
  /** Maximum percent (0-100) of the bounding box that may be covered by the matched colors. Default: 100 */
  colorsMaxPercentageOfRect?: number;
  /** Limit number of rects returned (largest first by matched pixels). Default: unlimited */
  maxRects?: number;
  /** Minimum matched pixels required in the component */
  minMatchedPixels?: number;
  /** Maximum matched pixels allowed in the component */
  maxMatchedPixels?: number;
}

export interface ConnectedRectInfo {
  x: number;
  y: number;
  width: number;
  height: number;
  /** Number of pixels in the connected component (matched colors) */
  matchPixelCount: number;
  /** Total area of the bounding box (width*height) */
  rectArea: number;
  /** matchPixelCount / rectArea * 100 */
  percentInRect: number;
  /** Per-color hit counts inside the component */
  colorHits: Record<HexColor, number>;
}

/**
 * Find bounding rectangles for connected regions comprised of a set of colors.
 * Connectivity is computed over matching pixels only. Bounding rect coverage
 * is validated against colorsMinPercentageOfRect/colorsMaxPercentageOfRect.
 */
export function findBoundingRectsForConnectedColors(
  imageBuffer: Buffer | Uint8Array,
  options: ConnectedColorsOptions
): ConnectedRectInfo[] {
  if (!options || !options.colors || options.colors.length === 0) {
    throw new Error('findBoundingRectsForConnectedColors requires at least one color');
  }
  const decoded = decodePng(imageBuffer);
  const width = decoded.width;
  const height = decoded.height;
  const data = decoded.data;

  const region = clampRegion(options.region ?? { x: 0, y: 0, width, height }, width, height);
  const alphaMin = options.alphaMin ?? 1;
  const excludeNearBlack = options.excludeNearBlack ?? false;
  const blackThreshold = options.blackClosenessThreshold ?? 0.9;
  const tolerance = options.tolerance ?? (options.defaultTolerance ?? 3);
  const adjacency = options.adjacency ?? 4;
  const minRegionPixels = Math.max(1, options.minRegionPixels ?? 1);
  const minPercent = options.colorsMinPercentageOfRect ?? 0;
  const maxPercent = options.colorsMaxPercentageOfRect ?? 100;
  const maxRects = options.maxRects ?? Number.POSITIVE_INFINITY;
  const minMatchedPixels = options.minMatchedPixels ?? 0;
  const maxMatchedPixels = options.maxMatchedPixels ?? Number.POSITIVE_INFINITY;

  const targetInputs: ColorInput[] = Array.isArray(options.colors) ? options.colors : [options.colors];
  const targetRGB: Array<{ r: number; g: number; b: number }> = targetInputs.map(toRGB);
  const targetColorLabels: string[] = targetRGB.map(rgbToHex);

  const idxOf = (x: number, y: number) => (y * width + x) * 4;
  const inRegion = (x: number, y: number) => x >= region.x && y >= region.y && x < region.x + region.width && y < region.y + region.height;

  // Visit map for matched pixels only
  const visited = new Uint8Array(width * height);
  const key = (x: number, y: number) => y * width + x;

  const neighbors4 = (x: number, y: number) => (
    [ [x-1, y], [x+1, y], [x, y-1], [x, y+1] ] as Array<[number, number]>
  );
  const neighbors8 = (x: number, y: number) => (
    [ [x-1,y-1],[x,y-1],[x+1,y-1], [x-1,y],[x+1,y], [x-1,y+1],[x,y+1],[x+1,y+1] ] as Array<[number, number]>
  );
  const getNeighbors = adjacency === 8 ? neighbors8 : neighbors4;

  const isMatched = (x: number, y: number): number | -1 => {
    const i = idxOf(x, y);
    const a = data[i + 3];
    if (a < alphaMin) return -1;
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    if (excludeNearBlack && blackCloseness(r, g, b) >= blackThreshold) return -1;
    for (let c = 0; c < targetRGB.length; c++) {
      if (isColorMatch({ r, g, b }, targetRGB[c] as { r: number; g: number; b: number }, tolerance)) return c;
    }
    return -1;
  };

  const results: ConnectedRectInfo[] = [];
  for (let y = region.y; y < region.y + region.height; y++) {
    for (let x = region.x; x < region.x + region.width; x++) {
      const k = key(x, y);
      if (visited[k]) continue;
      const colorIdx = isMatched(x, y);
      if (colorIdx === -1) continue;

      let minX = x, maxX = x, minY = y, maxY = y;
      let matchCount = 0;
  const colorHits: Record<HexColor, number> = Object.fromEntries(targetColorLabels.map(c => [c, 0]));
      const q: Array<[number, number]> = [[x, y]];
      visited[k] = 1;

      while (q.length) {
        const [cx, cy] = q.shift()!;
        // Tally
        const cidx = isMatched(cx, cy);
        if (cidx !== -1) {
          const label = targetColorLabels[cidx as number];
          colorHits[label]++;
          matchCount++;
        }
        // Bounds
        if (cx < minX) minX = cx; if (cx > maxX) maxX = cx;
        if (cy < minY) minY = cy; if (cy > maxY) maxY = cy;
        // Explore neighbors (only matched pixels and within region)
        for (const [nx, ny] of getNeighbors(cx, cy)) {
          if (!inRegion(nx, ny)) continue;
          const kk = key(nx, ny);
          if (visited[kk]) continue;
          const m = isMatched(nx, ny);
          if (m !== -1) {
            visited[kk] = 1;
            q.push([nx, ny]);
          }
        }
      }

  if (matchCount < minRegionPixels) continue;
  if (matchCount < minMatchedPixels) continue;
  if (matchCount > maxMatchedPixels) continue;
      const rectW = maxX - minX + 1;
      const rectH = maxY - minY + 1;
      const rectArea = rectW * rectH;
      const percent = (matchCount / rectArea) * 100;
      if (percent + 1e-9 < minPercent) continue;
      if (percent - 1e-9 > maxPercent) continue;

      results.push({ x: minX, y: minY, width: rectW, height: rectH, matchPixelCount: matchCount, rectArea, percentInRect: percent, colorHits });
    }
  }

  // Order by descending matchPixelCount then descending percentInRect
  results.sort((a, b) => (b.matchPixelCount - a.matchPixelCount) || (b.percentInRect - a.percentInRect));
  return results.slice(0, Math.max(0, Math.min(results.length, maxRects)));
}

// Back-compat alias spelling
export const findBoundingRectForConnectedColors = findBoundingRectsForConnectedColors;

export interface MultiColorQueryInput extends ColorCoverageQuery {
  colors: ColorInput[];
}

export interface PaletteCoveragePlan {
  queries: MultiColorQueryInput[];
  options?: ColorCoverageOptions;
}

export function buildPaletteQueries(
  palette: Record<string, ColorInput | ColorInput[]>,
  plan: Record<string, Pick<ColorCoverageExpectation, 'tolerance'>>
): ColorCoverageQuery[] {
  return Object.entries(plan).map(([label, config]) => {
    const colors = palette[label];
    if (!colors) {
      throw new Error(`Palette missing entry for query '${label}'`);
    }
    const colorArray = Array.isArray(colors) ? colors : [colors];
    return {
      colors: colorArray,
      tolerance: config.tolerance,
    };
  });
}

export interface CountPixelsOptions extends Pick<
  ColorCoverageOptions,
  'region' | 'alphaMin' | 'excludeNearBlack' | 'blackClosenessThreshold' | 'defaultTolerance'
> {
  colors: ColorInput | ColorInput[];
  tolerance?: number;
}

export function countPixelsInRect(
  imageBuffer: Buffer | Uint8Array,
  options: CountPixelsOptions
): { totalConsidered: number; totalMatched: number; perColor: Record<HexColor, number> } {
  if (!options || !options.colors) throw new Error('countPixelsInRect requires colors');
  const decoded = decodePng(imageBuffer);
  const width = decoded.width;
  const height = decoded.height;
  const data = decoded.data;

  const region = clampRegion(options.region ?? { x: 0, y: 0, width, height }, width, height);
  const alphaMin = options.alphaMin ?? 1;
  const excludeNearBlack = options.excludeNearBlack ?? false;
  const blackThreshold = options.blackClosenessThreshold ?? 0.9;
  const tolerance = options.tolerance ?? (options.defaultTolerance ?? 3);

  const inputs: ColorInput[] = Array.isArray(options.colors) ? options.colors : [options.colors];
  const rgbs = inputs.map(toRGB);
  const colorLabels: string[] = rgbs.map(rgbToHex);
  const perColor: Record<HexColor, number> = Object.fromEntries(colorLabels.map(c => [c, 0]));

  let totalConsidered = 0;
  let totalMatched = 0;
  for (let y = region.y; y < region.y + region.height; y++) {
    for (let x = region.x; x < region.x + region.width; x++) {
      const i = (y * width + x) * 4;
      const a = data[i + 3];
      if (a < alphaMin) continue;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      if (excludeNearBlack && blackCloseness(r, g, b) >= blackThreshold) continue;
      totalConsidered++;
      for (let c = 0; c < rgbs.length; c++) {
        if (isColorMatch({ r, g, b }, rgbs[c], tolerance)) {
          perColor[colorLabels[c]]++;
          totalMatched++;
          break;
        }
      }
    }
  }

  return { totalConsidered, totalMatched, perColor };
}
